import {
  EntityInformation,
  NameSyncStatus,
} from '@amzn/awsdev-docs-virtual-smiley-typescript-client';
import { Alert } from '@amzn/awsui-components-react';
import { ReactElement, useState } from 'react';

/**
 * Non-blocking warning shown when a listed entity's stored name no longer
 * matches the name committed in AWSSharedContentXML. Covers both service and
 * shared-text entities. The drift verdict is computed server-side and read
 * verbatim off nameSyncStatus; this component never recomputes it.
 */
export const NameSyncDriftBanner = ({
  entities,
}: {
  entities?: EntityInformation[];
}): ReactElement | null => {
  const [dismissed, setDismissed] = useState(false);
  const drifted = (entities ?? []).flatMap(
    (
      item
    ): {
      entityId: string;
      detail: string;
    }[] => {
      if (item.service?.nameSyncStatus === NameSyncStatus.DRIFT)
        return [
          {
            entityId: item.service.entityId,
            detail: item.service.offeringNameType,
          },
        ];
      if (item.sharedText?.nameSyncStatus === NameSyncStatus.DRIFT)
        return [
          {
            entityId: item.sharedText.entityId,
            detail: item.sharedText.locale,
          },
        ];
      return [];
    }
  );
  if (dismissed || !drifted.length) return null;
  return (
    <Alert
      visible={true}
      type='warning'
      dismissible={true}
      onDismiss={() => setDismissed(true)}
      header='Name drift detected in shared content'
    >
      <div>
        The following entity name(s) or localization state differ from the
        committed value in AWSSharedContentXML:
      </div>
      {drifted.map(({ entityId, detail }) => (
        <div key={entityId}>
          {entityId} ({detail})
        </div>
      ))}
    </Alert>
  );
};
